import type { AnyFormApi } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';

/** Non-reactive. In React components use {@link useFormErrors} instead. */
export function formErrors(form: AnyFormApi): Record<string, string> {
  return collectFormErrors(form.state.fieldMeta);
}

export function useFormErrors(form: AnyFormApi): Record<string, string> {
  const fieldMeta = useStore(form.store, (s) => s.fieldMeta);
  return collectFormErrors(fieldMeta);
}

function collectFormErrors(fieldMeta: Record<string, unknown> | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!fieldMeta) return result;

  for (const [field, meta] of Object.entries(fieldMeta)) {
    const errors = (meta as { errors?: unknown[] } | undefined)?.errors;
    if (!errors || errors.length === 0) continue;
    const message = extractMessage(errors[0]);
    if (message) result[field] = message;
  }

  return result;
}

/**
 * Error entries can be plain strings (custom validators) or issue objects
 * (Standard Schema, e.g. zod → `{ message, path, ... }`). Normalize both.
 */
function extractMessage(entry: unknown): string | undefined {
  if (typeof entry === 'string' && entry.length > 0) return entry;
  if (entry && typeof entry === 'object' && 'message' in entry) {
    const message = (entry as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return undefined;
}
